import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Icon,
  Image,
  Divider,
  Link,
} from '@chakra-ui/react';
import {
  FaCloud,
  FaSignOutAlt,
  FaMicrochip,
  FaArrowLeft,
  FaShieldAlt,
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Footer from './Footer';
import NavDropdowns from './NavDropdowns';

const DataPrivacy = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <Box>
      <Helmet>
        <title>Privacy Policy | SRS</title>
        <meta name="description" content="How SRS (Empirisch Tech GmbH) collects, processes, and protects your personal data under GDPR." />
        <link rel="canonical" href="https://srs.numericalai.net/privacy" />
        <meta property="og:title" content="Privacy Policy | SRS" />
        <meta property="og:description" content="How SRS (Empirisch Tech GmbH) collects, processes, and protects your personal data under GDPR." />
        <meta property="og:url" content="https://srs.numericalai.net/privacy" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SRS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy | SRS" />
        <meta name="twitter:description" content="How SRS (Empirisch Tech GmbH) collects, processes, and protects your personal data under GDPR." />
      </Helmet>
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200" position="sticky" top={0} zIndex={10}>
        <Container maxW="container.xl" py={4}>
          <HStack justify="space-between">
            <HStack spacing={4}>
              <Image
                src="/simple-logo.png"
                alt="SRS Logo"
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <HStack spacing={4}>
              <NavDropdowns />
              <Button 
                leftIcon={<FaArrowLeft />}
                variant="ghost" 
                as={RouterLink} 
                to="/"
              >
                Back to Home
              </Button>
              {user ? (
                <Button 
                leftIcon={<FaCloud />}
                colorScheme="brand"
                size="sm"
                variant="ghost" as={RouterLink} to="/dashboard">
                  Dashboard
                </Button>
              ) : (
                <Button variant="ghost" as={RouterLink} to="/signin">
                  Sign In
                </Button>
              )}
              {user ? (
                <Button 
                  leftIcon={<FaSignOutAlt />}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    logout();
                  }}
                >
                  Sign Out
                </Button>
              ) : (
                <Button colorScheme="brand" as={RouterLink} to="/create-user">
                  Get Started
                </Button>
              )}
            </HStack>
          </HStack>
        </Container>
      </Box>

      {/* Privacy Policy Content */}
      <Container maxW="container.lg" py={12}>
        <VStack spacing={8} align="stretch">
          <VStack spacing={4} textAlign="center">            
            <Heading as="h1" size="2xl" color="gray.800">
              Data Privacy Policy
            </Heading>
            <Text fontSize="lg" color="gray.600" fontWeight="bold">
              Last update: 05.01.2026
            </Text>
          </VStack>

          <Divider />

          <VStack spacing={6} align="stretch">
            <Box>
              <Heading size="lg" color="gray.800" mb={4}>
                1. Introduction
              </Heading>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                These data protection conditions apply to the services offered by SRS, in particular to those provided with the online platform  <Link href="https://srs.numericalai.net" color="brand.500" isExternal>https://srs.numericalai.net</Link> and all sub-pages accessible under this domain.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                We take the protection of your personal data very seriously. Data protection is a matter of trust. When processing your personal data, we carefully observe the legal provisions.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                We would like you to know which personal data we collect, process, store and use (hereinafter: "processed") for which purpose and, if necessary, pass them on.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                We therefore also inform you in detail about the data protection of our website and our online services, so that you feel safe when visiting and using our service.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={6}>
                SRS will process your data exclusively according to the following regulations. If you agree to the transfer of your data, different provisions may apply to the use of your data by the recipient than those contained in these data protection provisions.
              </Text>
            </Box>

            <Box>
              <Heading size="lg" color="gray.800" mb={4}>
                2. Responsible party
              </Heading>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
              The party responsible for the collection, processing and use of your personal data within the meaning of the Federal Data Protection Act is Empirisch Tech GmbH (limited liability), Managing Director, Dr. Wasif Masood, Rennweg 88, 1030 Vienna, Austria. <br />
              <Link 
                color="brand.500" 
                fontWeight="medium"
                _hover={{ textDecoration: "underline" }}
                onClick={() => navigate('/settings')}
                cursor="pointer"
              >
                Contact Form.
              </Link>

              </Text>
            </Box>

            <Box>
              <Heading size="lg" color="gray.800" mb={4}>
                3. Collection, processing and use of personal data
              </Heading>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                <Text as="span" fontWeight="bold">3.1</Text> According to applicable data protection laws, personal data are details about personal or factual circumstances of a certain or determinable natural person, for example name, address, telephone number including all data, which you disclose during the registration for the participation in the services offered by SRS. Anonymous information, on the other hand, is not personal data.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                For the provision and individualization of our services to your needs, it is necessary that we process certain personal data for the following purposes.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                <Text as="span" fontWeight="bold">3.2</Text> Within the scope of your registration we will process certain personal data (mandatory data). These are first name, surname, email address, password, affiliated company name and your role in the company. 
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                The e-mail address is also used as an alternative to the user name for your identification in the login area.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                In the course of your registration, we set up direct access to your stored data (user account) for each user. This access is password protected. 
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                In the user account you can view and manage your simulation results, actual code, uploads, downloads, total hours consumed, credits bopughts, etc. as well as your personal data.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                The access data must be treated confidentially and may not be made accessible to unauthorized third parties. We cannot accept any liability for improperly used access data.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                We collect, store and process your data for the administration and provision of your participation in the SRS services, to improve the services offered by SRS based on the information and feedback of the participants, to effectively answer your questions and to provide services, technical administration and for our own marketing purposes including e.g. surveys, competitions and contests as well as to send e-mails. The personal data will also be used to send you information regarding the services offered, the company, product information as well as for updates etc. SRS is entitled to use the e-mail address provided by you during registration.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                Your data will only be passed on to third parties if this is necessary for the purpose of participation in the services or the billing of fee-based services, if applicable, for any other purpose with your consent, or as required by law.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                Within the scope of order processing of chargeable services, for example, the service providers used by us (for example banks, payment services, credit card institutions) receive the necessary data for order and order processing. The data passed on in this way may only be used by our service providers for the fulfillment of their tasks. Any other use of the information is not permitted and will not be made by any of the service providers entrusted by us.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={6}>
                If you use our website for information purposes only, i.e. do not log in, register or otherwise provide us with information, we do not collect any personal data, with the exception of the data transmitted by your browser to enable you to visit the website.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                <Text as="span" fontWeight="bold">3.3</Text> With your explicit consent (via the checkbox in the Settings page), we may use your pyfr.ini and generated log files to; enhance simulation accuracy, improve AI-assisted features and conduct research to improve our services.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                This data usage is entirely optional for paid users and requires affirmative consent. Withdrawing consent does not affect any data processing that occurred while consent was active.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                With your consent, only pyfr.ini and generated logs are used to identify and learn from faulty parameter settings—helping others avoid similar mistakes, reducing unnecessary simulation runs, and ultimately lowering the carbon footprint. Your data is never shared with third parties or used to train external AI models.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
              <Text as="span" fontWeight="bold">3.4 Email Communications</Text> All official emails from SRS
                are sent from the domain @srs.numericalai.net.
                SRS is a platform operated by Empirisch Tech GmbH under the srs.numericalai.net domain.
                To ensure you receive important account notifications, please add our email address to your contacts or safe sender list.
              </Text>
            </Box>

            <Box>
              <Heading size="lg" color="gray.800" mb={4}>
                4. Cookies
              </Heading>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                Cookies are small text files that are stored on your computer. Cookies cannot run programs or transmit viruses to your computer. They serve to make the Internet offering as a whole more user-friendly and effective.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                In some areas of our website, we use such cookies, for example to recognize the preferences of visits to the website and to be able to design the website accordingly. In particular, this will facilitate navigation and improve the user-friendliness of the website. In addition, cookies help us to identify particularly popular areas of our services.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                Our website uses cookies for the following purposes:
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                Session cookies (temporary use), persistent cookies (temporary use), third party cookies (from third parties). Session cookies (also called transient cookies) are automatically deleted when you close your browser. These store a so-called session ID, with which different requests of your browser can be assigned to the common session. This will allow your computer to be recognized when you return to the website. Session cookies are deleted when you log out or close your browser.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                Persistent cookies are automatically deleted after a specified period, which may vary depending on the cookie. You can delete cookies at any time in the security settings of your browser.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                You can configure your browser settings according to your wishes and, for example, refuse the acceptance of third party cookies. However, we would like to point out that you may not be able to use all the functions of our website.
              </Text>
            </Box>

            <Box>
              <Heading size="lg" color="gray.800" mb={4}>
                5. Cloudflare Turnstile
              </Heading>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                We use Cloudflare Turnstile to protect our website from automated abuse and bots. 
                When you access pages with forms (such as sign-in or registration), Turnstile may 
                collect technical information including your IP address, browser type, and 
                interaction patterns to verify you are a human user.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={6}>
                This data is processed by Cloudflare, Inc. For more information, see Cloudflare's 
                privacy policy at <Link href="https://www.cloudflare.com/privacypolicy/" color="brand.500" isExternal>https://www.cloudflare.com/privacypolicy/</Link>.
              </Text>
            </Box>

            <Box>
              <Heading size="lg" color="gray.800" mb={4}>
                6. Payment Processing (Stripe)
              </Heading>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                We use Stripe, Inc. to process payments for our services. When you make a purchase, 
                Stripe collects your payment information (credit card number, billing address, email) 
                directly. We do not store your complete payment card details on our servers.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={6}>
                Stripe may use this information to process transactions, prevent fraud, and comply 
                with legal obligations. For more information, see Stripe's privacy policy at 
                <Link href="https://stripe.com/privacy" color="brand.500" isExternal>https://stripe.com/privacy</Link>.
              </Text>
            </Box>

            <Box>
              <Heading size="lg" color="gray.800" mb={4}>
                7. Use of Google Analytics
              </Heading>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                This website uses Google Analytics, a web analysis service of Google Inc. ("Google"). Google Analytics uses so-called cookies (see also 4. Cookies above), text files which are stored on your computer and which enable an analysis of the use of the website. The purpose of the use of Google Analytics is the statistical evaluation of the use of the website and the improvement of the website as well as our marketing measures as a legitimate interest.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                The information generated by the cookie about your use of this website is usually transferred to a Google server in the USA and stored there. Since IP anonymisation is activated for the website, Google will reduce your IP address in advance within the Member States of the European Union or in other states party to the Agreement on the European Economic Area. Only in exceptional cases will the full IP address be transmitted to a Google server in the USA and shortened there.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                On behalf of the operator of this website, Google will use this information to evaluate your use of the website, to compile reports on website activity and to provide the website operator with other services relating to website and Internet use. The IP address transmitted by your browser in the context of Google Analytics is not merged with other Google data.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={6}>
                You may refuse the use of cookies by selecting the appropriate settings on your browser, however please note that if you do this you may not be able to use the full functionality of this website. You can also prevent Google from collecting the data generated by the cookie and relating to your use of the website (including your IP address) and from processing this data by Google by downloading and installing the browser plugin available under the following link: <Link href="http://tools.google.com/dlpage/gaoptout?hl=en" color="brand.500" isExternal>http://tools.google.com/dlpage/gaoptout?hl=en</Link>.
              </Text>
            </Box>

            <Box>
              <Heading size="lg" color="gray.800" mb={4}>
                8. Use of Social Media Plugins
              </Heading>
              <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="bold">
                Facebook plugins
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                These web pages use plugins from the social network Facebook.com, which is operated by Facebook Inc. 1601S. California Ave, Palo Alto, CA 94304, USA ("Facebook").
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                If you call websites on our website that are provided with such a plugin, a connection to the Facebook servers is established and the plugin is displayed on the website by notifying your browser. This transfers to the Facebook server which of our Internet pages you have visited. Aimed use of Facebook is own marketing measures as a legitimate interest. If you are logged in as a Facebook member, Facebook assigns this information to your personal Facebook account. When using the plugin functions (e.g. clicking the "I like" button, posting a comment), this information is also assigned to your Facebook account, which you can only prevent by logging out before using the plugin. For more information about the purpose, collection and use of the data by Facebook, your rights and ways to protect your privacy, please see Facebook's privacy policy.
              </Text>

              <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="bold">
                Twitter plugin
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                Functions of the Twitter service have been integrated into our website and app. These features are offered by Twitter Inc., 1355 Market Street, Suite 900, San Francisco, CA 94103, USA. When you use Twitter and the "Retweet" function, the websites you visit are connected to your Twitter account and made known to other users. In doing so, data will also be transferred to Twitter. We would like to point out that, as the provider of these pages, we have no knowledge of the content of the data transmitted or how it will be used by Twitter. For more information on Twitter's privacy policy, please go to <Link href="https://twitter.com/privacy" color="brand.500" isExternal>https://twitter.com/privacy</Link>. Your privacy preferences with Twitter can be modified in your account settings at <Link href="https://twitter.com/account/settings" color="brand.500" isExternal>https://twitter.com/account/settings</Link>.
              </Text>

              <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="bold">
                LinkedIn plugin
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                Our site uses functions from the LinkedIn network. The service is provided by LinkedIn Corporation, 2029 Stierlin Court, Mountain View, CA 94043, USA. Each time one of our pages containing LinkedIn features is accessed, your browser establishes a direct connection to the LinkedIn servers. LinkedIn is informed that you have visited our web pages from your IP address. If you use the LinkedIn "Recommend" button and are logged into your LinkedIn account, it is possible for LinkedIn to associate your visit to our website to your user account. We would like to point out that, as the provider of these pages, we have no knowledge of the content of the data transmitted or how it will be used by LinkedIn. More information can be found in the LinkedIn privacy policy at <Link href="https://www.linkedin.com/legal/privacy-policy" color="brand.500" isExternal>https://www.linkedin.com/legal/privacy-policy</Link>.
              </Text>

              <Text color="gray.600" lineHeight="1.8" mb={6} fontWeight="bold">
                Instagram Social Plugins
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={6}>
                This website uses so-called Social Plugins ("Plugins") from Instagram, operated by Instagram LLC, 1601 Willow Road, Menlo Park, CA 94025, USA ("Instagram"). When you access a page of our website that contains such a plugin, your browser establishes a direct connection to Instagram's servers. This integration informs Instagram that your browser has called up the corresponding page of our website, even if you do not have an Instagram profile or are not currently logged in to Instagram. If you do not want Instagram to associate the information collected through our website directly with your Instagram account, you must log out of Instagram before visiting our website.
              </Text>
            </Box>

            <Box>
              <Heading size="lg" color="gray.800" mb={4}>
                9. YouTube
              </Heading>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                Our website uses plugins from the YouTube site operated by Google. The pages are operated by YouTube, LLC, 901 Cherry Ave, San Bruno, CA 94066, USA.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                If you visit one of our pages which features a YouTube plugin, a connection to the YouTube servers is established. The YouTube server is told which of our pages you have visited.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                If you are logged into your YouTube account, you enable YouTube to associate your surfing behavior directly with your personal profile. You can prevent this by logging out of your YouTube account.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={6}>
                The use of YouTube is in the interest of an appealing presentation of our online contents. This represents a legitimate interest within the meaning of applicable data protection regulations. For more information on how YouTube handles user data, please refer to YouTube's privacy statement at <Link href="https://www.google.de/intl/de/policies/privacy" color="brand.500" isExternal>https://www.google.de/intl/de/policies/privacy</Link>.
              </Text>
            </Box>

            <Box>
              <Heading size="lg" color="gray.800" mb={4}>
                10. Marketing and Advertising Technologies
              </Heading>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                With your consent, we use the following marketing and advertising technologies to measure 
                the effectiveness of our advertising campaigns, deliver relevant advertisements, and 
                build custom audiences for remarketing purposes. These services only activate when you 
                accept "Marketing Cookies" in your cookie preferences.
              </Text>

              <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="bold">
                Google Ads (Remarketing & Conversion Tracking)
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                We use Google Ads conversion tracking and remarketing services provided by Google LLC, 
                1600 Amphitheatre Parkway, Mountain View, CA 94043, USA. These services allow us to 
                display targeted advertisements to users who have previously visited our website when 
                they browse other websites in the Google Display Network. Google uses cookies to track 
                conversions and build remarketing audiences. Your data may be transferred to servers in 
                the USA. Google is certified under the EU-US Data Privacy Framework. For more information, 
                see Google's advertising privacy policy at{' '}
                <Link href="https://policies.google.com/technologies/ads" color="brand.500" isExternal>
                  https://policies.google.com/technologies/ads
                </Link>. You can opt out of personalized advertising at{' '}
                <Link href="https://adssettings.google.com" color="brand.500" isExternal>
                  https://adssettings.google.com
                </Link>.
              </Text>

              <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="bold">
                LinkedIn Insight Tag
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                We use the LinkedIn Insight Tag provided by LinkedIn Corporation, 1000 W. Maude Ave, 
                Sunnyvale, CA 94085, USA. This tag enables us to collect data about visits to our 
                website, including URL, referrer, IP address, device and browser characteristics, 
                and timestamp. This data is used for conversion tracking, retargeting, and analytics 
                of LinkedIn advertising campaigns. IP addresses are truncated before storage. 
                For more information, see LinkedIn's privacy policy at{' '}
                <Link href="https://www.linkedin.com/legal/privacy-policy" color="brand.500" isExternal>
                  https://www.linkedin.com/legal/privacy-policy
                </Link>. You can opt out at{' '}
                <Link href="https://www.linkedin.com/psettings/guest-controls/retargeting-opt-out" color="brand.500" isExternal>
                  https://www.linkedin.com/psettings/guest-controls/retargeting-opt-out
                </Link>.
              </Text>

              <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="bold">
                Meta (Facebook) Pixel
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                We use the Meta Pixel (formerly Facebook Pixel) provided by Meta Platforms Ireland Ltd., 
                4 Grand Canal Square, Dublin 2, Ireland. The Meta Pixel collects data about your 
                interactions with our website to measure the effectiveness of our advertising on Facebook 
                and Instagram, build custom audiences, and deliver targeted advertisements. Data collected 
                may include pages visited, actions taken, and device information. This data may be 
                transferred to Meta servers in the USA. For more information, see Meta's privacy policy at{' '}
                <Link href="https://www.facebook.com/privacy/policy/" color="brand.500" isExternal>
                  https://www.facebook.com/privacy/policy/
                </Link>. You can manage your ad preferences at{' '}
                <Link href="https://www.facebook.com/adpreferences" color="brand.500" isExternal>
                  https://www.facebook.com/adpreferences
                </Link>.
              </Text>

              <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="bold">
                Twitter (X) Pixel
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                We use the Twitter Pixel (X Pixel) provided by X Corp., 1355 Market Street, Suite 900, 
                San Francisco, CA 94103, USA. This tracking pixel allows us to measure the effectiveness 
                of our Twitter advertising campaigns, track conversions, and build audiences for 
                retargeting. Data collected includes page views, conversions, and device information. 
                For more information, see Twitter's privacy policy at{' '}
                <Link href="https://twitter.com/privacy" color="brand.500" isExternal>
                  https://twitter.com/privacy
                </Link>. You can adjust your personalization and data settings at{' '}
                <Link href="https://twitter.com/settings/account/personalization" color="brand.500" isExternal>
                  https://twitter.com/settings/account/personalization
                </Link>.
              </Text>

              <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="bold">
                Microsoft Advertising (Bing UET Tag)
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={6}>
                We use the Microsoft Universal Event Tracking (UET) tag provided by Microsoft Corporation, 
                One Microsoft Way, Redmond, WA 98052, USA. The UET tag enables us to track conversions 
                from Microsoft Advertising campaigns (Bing, MSN, and partner sites) and build remarketing 
                audiences. Data collected includes page visits, search terms that led to our website, 
                and conversion actions. For more information, see Microsoft's privacy statement at{' '}
                <Link href="https://privacy.microsoft.com/privacystatement" color="brand.500" isExternal>
                  https://privacy.microsoft.com/privacystatement
                </Link>. You can opt out of personalized advertising at{' '}
                <Link href="https://account.microsoft.com/privacy/ad-settings" color="brand.500" isExternal>
                  https://account.microsoft.com/privacy/ad-settings
                </Link>.
              </Text>
            </Box>

            <Box>
              <Heading size="lg" color="gray.800" mb={4}>
                11. Google Web Fonts
              </Heading>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                For uniform representation of fonts, this page uses web fonts provided by Google. When you open a page, your browser loads the required web fonts into your browser cache to display texts and fonts correctly.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                When you call up a page of our website that contains a social plugin, your browser makes a direct connection with Google servers. Google thus becomes aware that our web page was accessed via your IP address. The use of Google Web fonts is done in the interest of a uniform and attractive presentation of our website. This constitutes a justified interest pursuant to applicable data protection regulations.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={6}>
                If your browser does not support web fonts, a standard font is used by your computer. Further information about handling user data, can be found at <Link href="https://developers.google.com/fonts/faq" color="brand.500" isExternal>https://developers.google.com/fonts/faq</Link> and in Google's privacy policy at <Link href="https://www.google.com/policies/privacy" color="brand.500" isExternal>https://www.google.com/policies/privacy</Link>.
              </Text>
            </Box>

            <Box>
              <Heading size="lg" color="gray.800" mb={4}>
                12. Information on the newsletter and consents
              </Heading>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                With the following information we inform you about the contents of our newsletter as well as the registration, dispatch and statistical evaluation procedure and your rights of objection. By subscribing to our newsletter, you agree to the receipt and the described procedure.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="bold">
                Contents of the newsletter
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                We only send newsletters, e-mails and other electronic notifications, including promotional information (hereinafter: "newsletters") with the consent of the recipients or a legal permission. If the contents of a newsletter are specifically described within the scope of a registration, they are decisive for the consent of the users. In addition, our newsletters contain information about our products and services, blog posts, offers, competitions, updates and any activities related to our platform and our company.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                The registration for our newsletter takes place in a so-called double opt-in procedure. This means that after registration you will receive an e-mail asking you to confirm your registration. This confirmation is necessary so that nobody can log in with other e-mail addresses. The registration for the newsletter is recorded in order to be able to prove the registration process according to the legal requirements. This includes the storage of the login and confirmation time, as well as the IP address.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                To subscribe to the newsletter, simply enter your e-mail address.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                The newsletters contain a so-called "web beacon", i.e. a pixel large file, which is retrieved from the SRS server when the newsletter is opened. Within the scope of this retrieval, technical information, information about the browser and your system as well as your IP address and time of retrieval are initially collected. This information is used to technically improve the service based on the technical data or the target groups and their reader behavior based on their retrieval locations (which can be determined using the IP address) or access times. For technical reasons, this information can be assigned to the individual newsletter recipients. However, we do not strive to observe individual users. The evaluations serve us rather to recognize the reading habits of our users and to adapt our contents to them or to send different contents according to the interests of our users.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4} fontWeight="bold">
                Cancellation/Revocation Newsletter
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                You can cancel the receipt of our newsletter at any time, i.e. revoke your consent. You will find a link to cancel the newsletter at the end of each newsletter.
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={6} fontWeight="bold">
                Legal basis Data Protection Regulation
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={6}>
                In accordance with the provisions of applicable data protection regulations, we inform you that your consents to the sending of e-mail addresses are based on your explicit consent. The statistical surveys and analyses as well as the logging of the application procedure are carried out on the basis of our legitimate interests. We are interested in using a user-friendly and secure newsletter system that serves both our business interests and the expectations of users. We would also like to point out that you can object to the future processing of your personal data at any time in accordance with the statutory provisions. The objection may be lodged in particular against processing for direct marketing purposes.
              </Text>
            </Box>

            <Box>
              <Heading size="lg" color="gray.800" mb={4}>
                13. Rights of the persons concerned
              </Heading>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                You have the right:
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                • To request information about your personal data processed by us. In particular, you may request information about the purpose of processing, the category of personal data, the categories of recipients to whom your data have been or will be disclosed, the planned storage period, the existence of a right to rectification, deletion, restriction of processing or objection, the existence of a right of appeal, the origin of your data, if these have not been collected by us, and the existence of automated decision-making including profiling and, if applicable, meaningful information on their details;
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                • To demand without delay the correction of incorrect or complete personal data stored by us;
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                • To request the deletion of your personal data stored with us, unless the processing is necessary to exercise the right to freedom of expression and information, to fulfill a legal obligation, for reasons of public interest or to assert, exercise or defend legal claims;
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                • To demand the restriction of the processing of your personal data if you dispute the accuracy of the data, if the processing is unlawful but you refuse to delete the data and if you no longer require the data for the purpose of asserting, exercising or defending legal claims, or if you have lodged an objection against the processing;
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                • To request your personal data in a structured, commonly used, and machine-readable format, and to transmit that data to another controller where applicable;
              </Text>
              <Text color="gray.600" lineHeight="1.8" mb={6}>
                • To revoke your consent to us at any time. As a result, we are no longer allowed to continue processing data based on this consent in the future and to complain to a supervisory authority. As a rule, you can contact the supervisory authority at your usual place of residence or workplace or at our company headquarters. Please note that revoking consent does not affect the lawfulness of processing carried out before the revocation. Some data, such as billing records, may still be retained where required by legal obligations (e.g., tax or accounting laws).
              </Text>
            </Box>

            <Box>
              <Heading size="lg" color="gray.800" mb={4}>
                14. Deletion of data
              </Heading>
              <Text color="gray.600" lineHeight="1.8" mb={6}>
                Your personal data will be deleted if there are no legal storage obligations to the contrary and if you have asserted a claim for deletion, if the data is no longer required to fulfill the purpose for which it was stored or if its storage is inadmissible for other legal reasons. 
                All persoanal, usage and operational data is deleted upon deletion of the user account. We keep the billing data for seven years after the last purchase.
              </Text>
            </Box>

            <Box>
              <Heading size="lg" color="gray.800" mb={4}>
                15. Right of objection
              </Heading>
              <Text color="gray.600" lineHeight="1.8" mb={4}>
                If your personal data are processed on the basis of legitimate interests, you have the right to object to the processing of your personal data, provided that there are reasons for this which arise from your particular situation or the objection is directed against direct advertising. In the latter case, you have a general right of objection, which we will implement without specifying a particular situation.
                <br />
                <Link 
                  color="brand.500" 
                  fontWeight="medium"
                  _hover={{ textDecoration: "underline" }}
                  onClick={() => navigate('/settings')}
                  cursor="pointer"
                >
                  Contact Form.
                </Link>
              </Text>
            </Box>
          </VStack>

          <Divider />

          <VStack spacing={4} textAlign="center">
            <Text color="gray.500" fontSize="sm">
              By using SRS's services, you acknowledge that you have read, understood, and agree to this Privacy Policy.
            </Text>
          </VStack>
        </VStack>
      </Container>

      {/* Footer */}

      <Footer />
    </Box>
  );
};

export default DataPrivacy;
